import { useState } from "react";
import { toast } from "sonner";
import { Check, Eye, Trash2, UserPlus, X } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { REQ_STATUS, levelOf, requestDetail, requestTitle } from "@/lib/access";
import { fmtDateTime } from "@/lib/time";
import { SeeMore, useSeeMore } from "@/components/maintenance/admin/SeeMore";
import { StaffForm } from "./StaffForm";
import { StaffCard } from "./StaffCard";

const small = "flex items-center gap-1.5 border border-black/15 bg-white px-3 py-1.5 text-xs font-semibold transition-colors hover:border-[#0A0A0A]";


export const StaffManager = ({ staff, onChanged }) => {
  const { isAdmin, user: me } = useAdmin();
  const [form, setForm] = useState({ open: false, staff: null });
  const act = async (u, action, msg) => { try { await api.post(`/staff/${u.id}/${action}`); toast.success(msg); onChanged(); } catch (e) { toast.error(errText(e)); } };
  const terminate = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}'s account? This cannot be undone.`)) return;
    try { await api.delete(`/staff/${u.id}`); toast.success("Account terminated"); onChanged(); } catch (e) { toast.error(errText(e)); }
  };
  const team = staff.filter((u) => u.role !== "admin");
  return (
    <div data-testid="staff-manager">
      <button onClick={() => setForm({ open: true, staff: null })} className="mb-5 flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white hover:bg-[#E62314]" data-testid="staff-create">
        <UserPlus className="h-4 w-4" />{isAdmin ? "Create New Account" : "Request New Account"}</button>
      {!team.length ? <p className="border border-dashed border-black/20 p-8 text-center text-sm text-[#57534E]" data-testid="staff-empty">No staff accounts yet.</p> : (
        <ul className="space-y-5">
          {team.map((u) => (
            <StaffCard key={u.id} u={u} me={me} isAdmin={isAdmin} onEdit={() => setForm({ open: true, staff: u })} act={(a, m) => act(u, a, m)} onTerminate={() => terminate(u)} />
          ))}
        </ul>
      )}
      <StaffForm open={form.open} staff={form.staff} ownerEdit={isAdmin} onOpenChange={(o) => setForm((x) => ({ ...x, open: o }))} onSaved={onChanged} />
    </div>
  );
};

const ReqRow = ({ r, children, testId }) => {
  const [label, cls] = REQ_STATUS[r.status] || REQ_STATUS.pending;
  const showLevel = r.type === "folder" || (r.type === "upload" && !r.folder_name);
  return (
    <li className="flex w-full flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold">{requestTitle(r)}</p>
        <p className="mt-0.5 break-words text-sm">{requestDetail(r)}</p>
        <p className="mt-1 break-words text-xs text-[#57534E]">From {r.user_name || r.user_email} · {fmtDateTime(r.status === "pending" ? r.created_at : r.decided_at || r.created_at)}{showLevel && <> · {levelOf(r.level).label}</>}</p>
        {r.notes && <p className="mt-2 break-words text-xs text-[#3a3836]">Note: {r.notes}</p>}
        {r.reason && <p className="mt-1 break-words text-xs text-[#57534E]">Reason: {r.reason}</p>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
        <span className={`text-sm font-bold ${cls}`} data-testid={`${testId}-status`}>{label}</span>
        {children}
      </div>
    </li>
  );
};

export const RequestsInbox = ({ requests, onChanged }) => {
  const { isAdmin } = useAdmin();
  const [busy, setBusy] = useState("");
  const [reviewing, setReviewing] = useState(null);
  const pending = requests.filter((r) => r.status === "pending");
  const page = useSeeMore(requests.filter((r) => r.status !== "pending"));
  const decide = async (r, d) => {
    const reason = d === "decline" ? window.prompt("Reason for declining (optional, sent to them):") : "";
    if (reason === null) return;
    setBusy(r.id);
    try { await api.post(`/requests/${r.id}/${d}`, { reason: reason || "" }); toast.success(d === "approve" ? "Approved" : "Declined"); onChanged(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const removeOne = async (r) => {
    try { await api.delete(`/requests/${r.id}`); toast.success("Removed from history"); onChanged(); } catch (e) { toast.error(errText(e)); }
  };
  const clearAll = async () => {
    if (!window.confirm("Delete all decided requests from your history? Pending requests stay.")) return;
    try { await api.delete("/requests/history"); toast.success("History cleared"); onChanged(); } catch (e) { toast.error(errText(e)); }
  };
  const preview = async (r) => {
    try { const res = await api.get(`/requests/${r.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank", "noopener"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-8" data-testid="requests-inbox">
      <StaffForm open={!!reviewing} review={reviewing} ownerEdit={isAdmin} onOpenChange={(o) => !o && setReviewing(null)} onSaved={onChanged} />
      <div>
        <h3 className="mb-3 font-display text-lg font-extrabold">Waiting for you</h3>
        <div className="border border-black/15 bg-white">
          {!pending.length ? <p className="p-6 text-center text-sm text-[#57534E]" data-testid="requests-none">No requests waiting for you.</p> : <ul className="divide-y divide-black/5">{pending.map((r) => (
            <ReqRow key={r.id} r={r} testId={`request-${r.id}`}>
              {r.type === "upload" && <button onClick={() => preview(r)} className={small} data-testid={`request-${r.id}-preview`}><Eye className="h-3.5 w-3.5" />Preview</button>}
              {r.type === "profile" && r.file_name && <button onClick={() => preview(r)} className={small} data-testid={`request-${r.id}-proof`}><Eye className="h-3.5 w-3.5" />View proof</button>}
              {r.type === "account"
                ? <button disabled={busy === r.id} onClick={() => setReviewing(r)} className="flex items-center gap-1.5 bg-[#16A34A] px-3 py-1.5 text-xs font-bold text-white" data-testid={`request-${r.id}-review`}><Eye className="h-3.5 w-3.5" />Review &amp; approve</button>
                : <button disabled={busy === r.id} onClick={() => decide(r, "approve")} className="flex items-center gap-1.5 bg-[#16A34A] px-3 py-1.5 text-xs font-bold text-white" data-testid={`request-${r.id}-approve`}><Check className="h-3.5 w-3.5" />{r.type === "reactivation" ? "Reactivate" : r.type === "email_dispute" ? "Restore original email" : "Approve"}</button>}
              <button disabled={busy === r.id} onClick={() => decide(r, "decline")} className="flex items-center gap-1.5 bg-[#E62314] px-3 py-1.5 text-xs font-bold text-white" data-testid={`request-${r.id}-decline`}><X className="h-3.5 w-3.5" />Decline</button>
            </ReqRow>
          ))}</ul>}
        </div>
      </div>
      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-display text-lg font-extrabold">Decided</h3>
          {page.total > 0 && <button onClick={clearAll} className="flex items-center gap-1.5 text-xs font-semibold text-[#57534E] hover:text-[#E62314]" data-testid="requests-clear-history"><Trash2 className="h-3.5 w-3.5" />Clear history</button>}
        </div>
        <div className="border border-black/15 bg-white">
          {!page.total ? <p className="p-6 text-center text-sm text-[#57534E]">Nothing yet.</p> : <ul className="divide-y divide-black/5">{page.shown.map((r) => (
            <ReqRow key={r.id} r={r} testId={`decided-${r.id}`}>
              <button onClick={() => removeOne(r)} aria-label="Delete from history" className="flex h-8 w-8 items-center justify-center rounded text-[#57534E] hover:bg-black/10 hover:text-[#E62314]" data-testid={`decided-${r.id}-delete`}><Trash2 className="h-4 w-4" /></button>
            </ReqRow>
          ))}</ul>}
          <SeeMore {...page} testId="requests-decided" />
        </div>
      </div>
    </div>
  );
};
